import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/presentation/contexts/AuthContext';
import {
  formatNovedadPrice,
  novedadCopyKey,
  usePanelNovedades,
  type PanelNovedad,
} from '@/presentation/hooks/usePanelNovedades';

const lastSeenStorageKey = (username: string) =>
  `coofit.panel.novedades.lastSeenId.${username || 'anon'}`;

function readLastSeenId(username: string): number {
  if (typeof window === 'undefined') return 0;
  const raw = window.localStorage.getItem(lastSeenStorageKey(username));
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function writeLastSeenId(username: string, id: number): void {
  if (typeof window === 'undefined' || id < 1) return;
  window.localStorage.setItem(lastSeenStorageKey(username), String(id));
}

export const HeaderNovedadesMenu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const username = user?.username ?? '';
  const { novedades, error } = usePanelNovedades(20);
  const [open, setOpen] = useState(false);
  const [lastSeenId, setLastSeenId] = useState(() => readLastSeenId(username));
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setLastSeenId(readLastSeenId(username));
  }, [username]);

  const unreadCount = useMemo(
    () => novedades.filter((item) => item.id > lastSeenId).length,
    [novedades, lastSeenId],
  );

  const markSeen = () => {
    const maxId = novedades.reduce((max, item) => Math.max(max, item.id), 0);
    if (maxId > lastSeenId) {
      writeLastSeenId(username, maxId);
      setLastSeenId(maxId);
    }
  };

  const toggleOpen = () => {
    setOpen((current) => {
      const next = !current;
      if (next) markSeen();
      return next;
    });
  };

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target || !rootRef.current) return;
      if (!rootRef.current.contains(target)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const goToDashboard = () => {
    setOpen(false);
    navigate('/dashboard');
  };

  return (
    <div className="relative flex-shrink-0" ref={rootRef}>
      <button
        type="button"
        onClick={toggleOpen}
        className="panel-touch relative inline-flex rounded-xl text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-gray-800"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('layout.openNotifications')}
        title={t('layout.openNotifications')}
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 ? (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-teal-600 px-1 text-[10px] font-bold leading-none text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-slate-950/20 md:hidden" aria-hidden />
          <div
            role="menu"
            className="fixed inset-x-3 top-[3.75rem] z-50 flex max-h-[min(28rem,70vh)] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-2xl shadow-slate-900/15 dark:border-gray-700 dark:bg-gray-800 md:absolute md:inset-x-auto md:right-0 md:top-full md:mt-2 md:w-[22.5rem]"
          >
            <div className="border-b border-slate-100 px-4 py-3 dark:border-gray-700">
              <p className="text-sm font-semibold text-slate-900 dark:text-white">
                {t('pages.dashboard.novedades.title')}
              </p>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                {t('pages.dashboard.novedades.subtitle')}
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {error ? (
                <p className="px-4 py-6 text-sm text-slate-500 dark:text-slate-400">
                  {t('pages.dashboard.novedades.error')}
                </p>
              ) : novedades.length === 0 ? (
                <p className="px-4 py-6 text-sm text-slate-500 dark:text-slate-400">
                  {t('pages.dashboard.novedades.empty')}
                </p>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-gray-700">
                  {novedades.map((item) => (
                    <NovedadRow key={item.id} item={item} />
                  ))}
                </ul>
              )}
            </div>

            <div className="border-t border-slate-100 p-2 dark:border-gray-700">
              <button
                type="button"
                onClick={goToDashboard}
                className="w-full rounded-xl px-3 py-2 text-center text-sm font-medium text-teal-700 transition hover:bg-teal-50 dark:text-teal-300 dark:hover:bg-teal-950/40"
              >
                {t('pages.dashboard.novedades.viewAll')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const NovedadRow: React.FC<{ item: PanelNovedad }> = ({ item }) => {
  const { t } = useTranslation();
  const price = formatNovedadPrice(item.precioUnitario);
  const when = item.fechaCreacion ? new Date(item.fechaCreacion).toLocaleString() : '';

  return (
    <li className="px-4 py-2.5">
      <p className="text-sm text-slate-800 dark:text-slate-100">
        {t(novedadCopyKey(item.tipo), { clinic: item.nombreClinica, price })}
      </p>
      {when ? (
        <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">{when}</p>
      ) : null}
    </li>
  );
};
